/**
 * EGUI - the parts of the page you touch: the neck, the knobs, the pedals.
 *
 * THE NECK IS DRAWN IN PIXELS OF ITS OWN BOX
 * ------------------------------------------
 * A whole 22-fret neck scaled into a phone's width gives strings 13 px apart -
 * too close to hit one with a finger. So the SVG's viewBox is the element's
 * real pixel size, and the number of frets shown is whatever fits at a
 * playable width (~44 px per fret on touch, 34 px with a mouse); the position
 * slider moves that window up the neck, the way a hand does. Frets keep their
 * real proportions inside the window (2^(-k/12) spacing), which is what a
 * guitarist's eye expects.
 *
 * Nothing here makes sound: the neck reports presses, drags and releases as
 * (string, fret) and the app decides what they mean (note, bend, slide,
 * hammer-on).
 */
(function (root, factory) {
    var api = factory();
    if (typeof globalThis !== 'undefined') globalThis.EGUI = api;
    if (typeof window !== 'undefined') window.EGUI = api;
}(this, function () {
    'use strict';

    var NS = 'http://www.w3.org/2000/svg';
    var LETTERS = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
    /* Do-re-mi in the page's language (the page hands its seven syllables to
       setSolfege): the neck used to print Cyrillic До/Ре on every locale while
       the menu offered "Do Re Mi" / "도 레 미". */
    var SOLF7 = ['До', 'Ре', 'Ми', 'Фа', 'Соль', 'Ля', 'Си'];
    var SOLFEGE = [];
    function setSolfege(list) {
        if (list && list.length === 7) SOLF7 = list.slice();
        SOLFEGE = [0, 0, 1, 1, 2, 3, 3, 4, 4, 5, 5, 6].map(function (l, pc) { return SOLF7[l] + (pc === 1 || pc === 3 || pc === 6 || pc === 8 || pc === 10 ? '♯' : ''); });
    }
    setSolfege(null);
    /* a name spelled by the scale ("G#", "Ab" - EGPresets.scaleNames) in the
       neck's style */
    function spelledName(name, style) {
        var L = 'CDEFGAB'.indexOf(name.charAt(0));
        var acc = name.slice(1).replace(/#/g, '♯').replace(/b/g, '♭');
        return (style === 'solfege' ? SOLF7[L] : name.charAt(0)) + acc;
    }

    function el(name, attrs, parent) {
        var e = document.createElementNS(NS, name);
        if (attrs) for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) e.setAttribute(k, attrs[k]);
        if (parent) parent.appendChild(e);
        return e;
    }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function noteName(midi, style) {
        var pc = ((midi % 12) + 12) % 12;
        return (style === 'solfege' ? SOLFEGE : LETTERS)[pc];
    }

    // ────────────────────────────────── neck ──────────────────────────────
    function Neck(svg, opts) {
        opts = opts || {};
        this.svg = svg;
        this.opts = opts;
        this.strings = 6;
        this.frets = 22;
        this.tuning = [64, 59, 55, 50, 45, 40];
        this.capo = 0;
        this.model = 's';
        this.lefty = false;
        this.labels = false;
        this.nameStyle = 'letters';
        this.pos = 0;                 // first fret shown (0 = the nut)
        this.scale = null;            // {pcs:[], root}
        this.chord = null;            // pcs
        this.targets = [];            // [{s, fret}]
        this.cursor = { s: 2, f: 5, on: false };
        this.fitH = null;             // height to fill (play mode), else rows by pointer type
        this.pressHandlers = [];
        this.pointers = {};
        this.geo = null;
        this.layer = {};
        var self = this;
        /* The box width comes from the observer (layout is clean when it
           runs): reading getBoundingClientRect() in render() forced a layout
           of the whole page right after a genre switch had changed half of
           it - 40 ms of a 160 ms switch at 4x CPU. */
        this._boxW = 0;
        this._ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(function (en) {
            var w = en[0] && en[0].borderBoxSize && en[0].borderBoxSize[0] ? en[0].borderBoxSize[0].inlineSize : svg.parentNode.getBoundingClientRect().width;
            if (Math.round(w) === Math.round(self._boxW) && self.geo) return;
            self._boxW = w;
            self.render();
        }) : null;
        if (this._ro) this._ro.observe(svg.parentNode);
        else window.addEventListener('resize', function () { self._boxW = 0; self.render(); });
        this._bind();
    }

    /* Redrawn only when something it draws changed: a genre switch hands the
       same strings, frets and names most of the time. */
    Neck.prototype.set = function (o) {
        var changed = !this.geo;
        for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) {
            var a = this[k], b = o[k];
            if (Array.isArray(a) && Array.isArray(b) ? a.join(',') !== b.join(',') : a !== b) changed = true;
            this[k] = Array.isArray(b) ? b.slice() : b;
        }
        if (changed) this.render();
    };

    /* How many frets fit at a playable width, and which ones are shown. */
    Neck.prototype._layout = function () {
        var bw = this._boxW || this.svg.parentNode.getBoundingClientRect().width;
        var W = Math.max(280, Math.round(bw || 800));
        var touch = window.matchMedia && window.matchMedia('(pointer:coarse)').matches;
        var rowH = touch ? 40 : (W < 700 ? 36 : 32);
        var top = 18, bottom = 28;
        var n = this.strings;
        // play mode hands the neck a height to fill: strings spread to it
        if (this.fitH) rowH = clamp(Math.floor((this.fitH - top - bottom) / n), 24, 64);
        var H = top + rowH * n + bottom;
        var openW = 46;               // the open-string zone left of the nut
        var minFret = touch ? 46 : 34;
        var avail = W - openW - 14;
        // real proportions: fret k at L*(1-2^(-k/12)); choose the largest window
        // [a, b] starting at `pos` whose SMALLEST fret is still playable
        var a = clamp(this.pos, 0, this.frets - 3);
        var b = this.frets;
        /* Fret k sits at L(k) = 1 - 2^(-k/12) on a real neck. Blended with an
           even spacing (35 %) so a desktop sees two octaves without the top
           frets shrinking below a fingertip, while the eye still reads a
           guitar neck (wide low frets, narrowing up the neck). */
        var BL = 0.35;
        function pos(k) { return (1 - BL) * (1 - Math.pow(2, -k / 12)) + BL * (k / 24) * 0.75; }
        function spanPx(a, b) { return { total: pos(b) - pos(a), last: pos(b) - pos(b - 1) }; }
        while (b - a > 3) {
            var sp = spanPx(a, b);
            if (avail * (sp.last / sp.total) >= minFret) break;
            b--;
        }
        this.visible = { a: a, b: b };
        var sp2 = spanPx(a, b);
        var x0 = openW;
        var fx = [];
        for (var k = a; k <= b; k++) fx.push(x0 + (pos(k) - pos(a)) / sp2.total * avail);
        var ys = [];
        for (var s = 0; s < n; s++) ys.push(top + rowH * (s + 0.5));
        this.geo = { W: W, H: H, rowH: rowH, top: top, fx: fx, ys: ys, openW: openW, a: a, b: b, x0: x0, xEnd: x0 + avail };
        return this.geo;
    };
    Neck.prototype._X = function (x) { return this.lefty ? this.geo.W - x : x; };

    Neck.prototype.render = function () {
        var svg = this.svg;
        var g = this._layout();
        while (svg.firstChild) svg.removeChild(svg.firstChild);
        svg.setAttribute('viewBox', '0 0 ' + g.W + ' ' + g.H);
        svg.style.height = g.H + 'px';
        var X = this._X.bind(this);
        var maple = this.model === 's' || this.model === 't';
        var defs = el('defs', null, svg);
        var wood = el('linearGradient', { id: 'egWood', x1: 0, y1: 0, x2: 0, y2: 1 }, defs);
        el('stop', { offset: '0', 'stop-color': maple ? '#e9cc95' : '#4a2c1c' }, wood);
        el('stop', { offset: '0.5', 'stop-color': maple ? '#dcb877' : '#3a2216' }, wood);
        el('stop', { offset: '1', 'stop-color': maple ? '#d2ab69' : '#2c190f' }, wood);
        // Strings are NOT drawn with a gradient: an objectBoundingBox gradient
        // on a perfectly horizontal line has a zero-height box and renders
        // nothing at all (the neck showed no strings). Metal is faked with a
        // body, a highlight and a shadow instead.
        // board
        var bx0 = Math.min(X(g.fx[0]), X(g.fx[g.fx.length - 1]));
        var bw = Math.abs(X(g.fx[g.fx.length - 1]) - X(g.fx[0]));
        el('rect', { x: bx0, y: g.top - 4, width: bw, height: g.rowH * this.strings + 8, fill: 'url(#egWood)', rx: 3 }, svg);
        // grain
        var grain = el('g', { opacity: maple ? 0.12 : 0.18, stroke: maple ? '#8a5a2b' : '#000', 'stroke-width': 1, fill: 'none' }, svg);
        for (var gi = 0; gi < 7; gi++) {
            var yy = g.top + (g.rowH * this.strings) * (gi + 0.5) / 7;
            el('path', { d: 'M' + bx0 + ' ' + yy + ' q ' + bw / 3 + ' ' + (gi % 2 ? 4 : -4) + ' ' + bw * 2 / 3 + ' 0 t ' + bw / 3 + ' 0' }, grain);
        }
        // inlays
        var inl = el('g', { fill: maple ? '#1f1f22' : '#efe7d2', opacity: 0.9 }, svg);
        var dots = [3, 5, 7, 9, 15, 17, 19, 21];
        var mid = g.top + g.rowH * this.strings / 2;
        for (var k = g.a + 1; k <= g.b; k++) {
            var xm = (g.fx[k - g.a - 1] + g.fx[k - g.a]) / 2;
            if (dots.indexOf(k) >= 0) el('circle', { cx: X(xm), cy: mid, r: Math.min(7, g.rowH * 0.2) }, inl);
            if (k === 12 || k === 24) {
                el('circle', { cx: X(xm), cy: g.top + g.rowH * this.strings * 0.28, r: Math.min(7, g.rowH * 0.2) }, inl);
                el('circle', { cx: X(xm), cy: g.top + g.rowH * this.strings * 0.72, r: Math.min(7, g.rowH * 0.2) }, inl);
            }
        }
        // frets and nut
        var fr = el('g', null, svg);
        for (var f = 0; f < g.fx.length; f++) {
            var fk = g.a + f;
            if (fk === 0) el('rect', { x: X(g.fx[0]) - (this.lefty ? 0 : 7), y: g.top - 5, width: 7, height: g.rowH * this.strings + 10, fill: '#efe6cf', rx: 1.5 }, fr);
            else el('line', { x1: X(g.fx[f]), y1: g.top - 4, x2: X(g.fx[f]), y2: g.top + g.rowH * this.strings + 4, stroke: '#c9ced6', 'stroke-width': 3, 'stroke-linecap': 'round' }, fr);
        }
        // capo
        if (this.capo > 0 && this.capo > g.a && this.capo <= g.b) {
            var cx = (g.fx[this.capo - g.a - 1] + g.fx[this.capo - g.a]) / 2 + (g.fx[this.capo - g.a] - g.fx[this.capo - g.a - 1]) * 0.3;
            el('rect', { x: X(cx) - 5, y: g.top - 8, width: 10, height: g.rowH * this.strings + 16, rx: 5, fill: '#2b2b2f', stroke: '#777', 'stroke-width': 1 }, fr);
        }
        // fret numbers
        var nums = el('g', { fill: '#aeb4bd', 'font-size': 12, 'font-weight': 700, 'text-anchor': 'middle' }, svg);
        for (var k2 = Math.max(1, g.a + 1); k2 <= g.b; k2++) {
            if (dots.indexOf(k2) < 0 && k2 !== 12 && k2 !== 24 && k2 !== 1 && k2 !== g.a + 1) continue;
            var xm2 = (g.fx[k2 - g.a - 1] + g.fx[k2 - g.a]) / 2;
            el('text', { x: X(xm2), y: g.H - 8 }, nums).textContent = String(k2);
        }
        // strings + names
        this.stringEls = [];
        var names = el('g', { fill: '#dfe3e8', 'font-size': 13, 'font-weight': 800, 'text-anchor': 'middle' }, svg);
        for (var s = 0; s < this.strings; s++) {
            var y = g.ys[s];
            var woundS = s >= 3 || (this.strings === 7 && s >= 3);
            var w = 1.1 + s * (this.strings === 7 ? 0.42 : 0.5);
            var xs0 = X(g.a === 0 ? g.fx[0] - 3 : 0), xs1 = X(g.W);
            el('line', { x1: xs0, y1: y + w * 0.5 + 1.2, x2: xs1, y2: y + w * 0.5 + 1.2, stroke: '#000', 'stroke-width': 1.2, opacity: 0.35 }, svg);
            var line = el('line', { x1: xs0, y1: y, x2: xs1, y2: y, stroke: woundS ? '#b9ab8d' : '#d3d9e0', 'stroke-width': w }, svg);
            el('line', { x1: xs0, y1: y - w * 0.22, x2: xs1, y2: y - w * 0.22, stroke: '#fff', 'stroke-width': Math.max(0.5, w * 0.28), opacity: woundS ? 0.35 : 0.55 }, svg);
            if (woundS) el('line', { x1: xs0, y1: y, x2: xs1, y2: y, stroke: '#6b5d45', 'stroke-width': w * 0.9, 'stroke-dasharray': '1 1.6', opacity: 0.35 }, svg);
            this.stringEls.push(line);
            var midi = this.tuning[s] + this.capo;
            el('text', { x: X(g.openW / 2 - 4), y: y + 4.5 }, names).textContent = noteName(midi, this.nameStyle);
        }
        // overlays
        this.layer.scale = el('g', null, svg);
        this.layer.targets = el('g', null, svg);
        this.layer.hold = el('g', null, svg);
        this.layer.hits = el('g', null, svg);
        this.layer.cursor = el('g', null, svg);
        this._drawScale();
        this._drawTargets();
        this._drawCursor();
        if (this.opts.onRender) this.opts.onRender(g);
    };

    /* Centre of the cell for (string, fret) in screen coords, or null. */
    Neck.prototype.cellXY = function (s, fret) {
        var g = this.geo;
        if (!g || s < 0 || s >= this.strings) return null;
        var x;
        if (fret === 0) {
            if (g.a !== 0) return null;
            x = g.openW / 2 + 12;
        } else {
            if (fret <= g.a || fret > g.b) return null;
            x = g.fx[fret - g.a] - (g.fx[fret - g.a] - g.fx[fret - g.a - 1]) * 0.42;
        }
        return { x: this._X(x), y: g.ys[s] };
    };

    Neck.prototype._drawScale = function () {
        var L = this.layer.scale;
        if (!L) return;
        while (L.firstChild) L.removeChild(L.firstChild);
        if (!this.scale && !this.labels) return;
        var g = this.geo, r = Math.min(11, g.rowH * 0.32);
        for (var s = 0; s < this.strings; s++) {
            for (var f = (g.a === 0 ? 0 : g.a + 1); f <= g.b; f++) {
                var midi = this.tuning[s] + this.capo + f;
                var pc = ((midi % 12) + 12) % 12;
                var inScale = this.scale && this.scale.pcs.indexOf(pc) >= 0;
                if (!inScale && !this.labels) continue;
                var p = this.cellXY(s, f);
                if (!p) continue;
                var isRoot = this.scale && pc === this.scale.root;
                var inChord = this.chord && this.chord.indexOf(pc) >= 0;
                if (inScale) {
                    el('circle', { cx: p.x, cy: p.y, r: r, fill: isRoot ? '#ff6b5a' : '#4f9dff', opacity: isRoot ? 0.9 : 0.62,
                        stroke: inChord ? '#fff' : 'none', 'stroke-width': inChord ? 2 : 0 }, L);
                } else {
                    el('circle', { cx: p.x, cy: p.y, r: r * 0.8, fill: 'rgba(0,0,0,.35)' }, L);
                }
                if (this.labels || isRoot) {
                    var tx = el('text', { x: p.x, y: p.y + 4, 'text-anchor': 'middle', 'font-size': 11, 'font-weight': 800, fill: '#fff' }, L);
                    tx.textContent = inScale && this.scale.names && this.scale.names[pc] ? spelledName(this.scale.names[pc], this.nameStyle) : noteName(midi, this.nameStyle);
                }
            }
        }
    };
    Neck.prototype.setScale = function (scale, chordPcs) { this.scale = scale; this.chord = chordPcs || null; this._drawScale(); };

    Neck.prototype._drawTargets = function () {
        var L = this.layer.targets;
        if (!L) return;
        while (L.firstChild) L.removeChild(L.firstChild);
        var r = Math.min(13, this.geo.rowH * 0.38);
        for (var i = 0; i < this.targets.length; i++) {
            var t = this.targets[i], p = this.cellXY(t.s, t.fret);
            if (!p) continue;
            el('circle', { cx: p.x, cy: p.y, r: r, fill: 'none', stroke: i === 0 ? '#ffcf4a' : 'rgba(255,207,74,.5)', 'stroke-width': i === 0 ? 3 : 2 }, L);
        }
    };
    Neck.prototype.setTargets = function (list) { this.targets = list || []; this._drawTargets(); };

    Neck.prototype._drawCursor = function () {
        var L = this.layer.cursor;
        if (!L) return;
        while (L.firstChild) L.removeChild(L.firstChild);
        if (!this.cursor.on) return;
        var p = this.cellXY(this.cursor.s, this.cursor.f);
        if (!p) return;
        el('rect', { x: p.x - 16, y: p.y - 14, width: 32, height: 28, rx: 7, fill: 'none', stroke: '#7aa7ff', 'stroke-width': 2.5 }, L);
    };

    /* A played note: a marker on the fret and the string lit from there to the bridge. */
    Neck.prototype.hit = function (s, fret, ms) {
        var L = this.layer.hits;
        if (!L) return;
        var g = this.geo, p = this.cellXY(s, fret);
        var grp = el('g', { class: 'hit' }, L);
        var y = g.ys[s];
        var xFrom = p ? p.x : this._X(g.x0);
        el('line', { x1: xFrom, y1: y, x2: this._X(g.xEnd + 10), y2: y, stroke: '#ffcf4a', 'stroke-width': 2.5 + s * 0.4, opacity: 0.55 }, grp);
        if (p) el('circle', { cx: p.x, cy: p.y, r: Math.min(12, g.rowH * 0.34), fill: '#ffcf4a' }, grp);
        setTimeout(function () { if (grp.parentNode) grp.parentNode.removeChild(grp); }, ms || 420);
    };
    Neck.prototype.hold = function (id, s, fret) {
        var L = this.layer.hold;
        if (!L) return;
        var old = L.querySelector('[data-id="' + id + '"]');
        if (old) old.parentNode.removeChild(old);
        if (s === null || s === undefined) return;
        var p = this.cellXY(s, fret);
        if (!p) return;
        var c = el('circle', { cx: p.x, cy: p.y, r: Math.min(13, this.geo.rowH * 0.36), fill: 'none', stroke: '#fff', 'stroke-width': 2.5, 'data-id': id }, L);
        return c;
    };

    /* Screen point -> {s, fret, x, y} (fret 0 = open zone). */
    Neck.prototype.locate = function (clientX, clientY) {
        var g = this.geo;
        if (!g) return null;
        var r = this.svg.getBoundingClientRect();
        var x = (clientX - r.left) * (g.W / r.width);
        var y = (clientY - r.top) * (g.H / r.height);
        if (this.lefty) x = g.W - x;
        var s = Math.round((y - g.top) / g.rowH - 0.5);
        s = clamp(s, 0, this.strings - 1);
        var fret;
        if (x < g.fx[0]) fret = g.a === 0 ? 0 : g.a;
        else {
            fret = g.b;
            for (var i = 1; i < g.fx.length; i++) if (x < g.fx[i]) { fret = g.a + i; break; }
        }
        return { s: s, fret: fret, x: x, y: y, rowH: g.rowH };
    };

    Neck.prototype.on = function (fn) { this.pressHandlers.push(fn); };
    Neck.prototype._emit = function (type, data) {
        for (var i = 0; i < this.pressHandlers.length; i++) this.pressHandlers[i](type, data);
    };
    Neck.prototype._bind = function () {
        var self = this, svg = this.svg;
        svg.addEventListener('pointerdown', function (e) {
            if (e.button > 0) return;
            e.preventDefault();
            var loc = self.locate(e.clientX, e.clientY);
            if (!loc) return;
            try { svg.setPointerCapture(e.pointerId); } catch (x) {}
            self.pointers[e.pointerId] = { start: loc, last: loc, t: performance.now() };
            self._emit('down', { id: e.pointerId, s: loc.s, fret: loc.fret, pointerType: e.pointerType, pressure: e.pressure });
        });
        svg.addEventListener('pointermove', function (e) {
            var p = self.pointers[e.pointerId];
            if (!p) return;
            var loc = self.locate(e.clientX, e.clientY);
            if (!loc) return;
            var dy = Math.abs(loc.y - p.start.y) / loc.rowH;
            self._emit('move', { id: e.pointerId, s: p.start.s, fret: loc.fret, startFret: p.start.fret, bendRows: dy });
            p.last = loc;
        });
        function up(e) {
            var p = self.pointers[e.pointerId];
            if (!p) return;
            delete self.pointers[e.pointerId];
            self._emit('up', { id: e.pointerId, s: p.start.s, fret: p.last.fret });
        }
        svg.addEventListener('pointerup', up);
        svg.addEventListener('pointercancel', up);
        /* Enter plays the note under the cursor like a finger: down on the key,
           up when the key is released - a short press rings, a held one damps on
           release. It used to send a synthetic up after 350 ms, longer than the
           tap threshold (260 ms), so every keyboard or screen-reader note was cut
           to 0.35 s; the key's auto-repeat also retriggered the note. */
        var kbdHeld = null;
        function kbdUp() { if (kbdHeld) { var h = kbdHeld; kbdHeld = null; self._emit('up', { id: 'kbd', s: h.s, fret: h.f }); } }
        svg.addEventListener('focus', function () { self.cursor.on = true; self._drawCursor(); });
        svg.addEventListener('blur', function () { kbdUp(); self.cursor.on = false; self._drawCursor(); });
        svg.addEventListener('keyup', function (e) { if (e.key === 'Enter') kbdUp(); });
        svg.addEventListener('keydown', function (e) {
            var c = self.cursor, moved = false;
            if (e.key === 'ArrowUp') { c.s = Math.max(0, c.s - 1); moved = true; }
            else if (e.key === 'ArrowDown') { c.s = Math.min(self.strings - 1, c.s + 1); moved = true; }
            else if (e.key === 'ArrowLeft') { c.f = Math.max(0, c.f - (self.lefty ? -1 : 1)); moved = true; }
            else if (e.key === 'ArrowRight') { c.f = Math.min(self.frets, c.f + (self.lefty ? -1 : 1)); moved = true; }
            else if (e.key === 'Enter') {
                e.preventDefault(); e.stopPropagation();
                if (e.repeat || kbdHeld) return;
                kbdHeld = { s: c.s, f: c.f };
                self._emit('down', { id: 'kbd', s: c.s, fret: c.f, pointerType: 'keyboard' });
                return;
            }
            if (moved) {
                e.preventDefault(); e.stopPropagation();
                c.f = clamp(c.f, 0, self.frets);
                if (c.f !== 0 && (c.f <= self.visible.a || c.f > self.visible.b)) {
                    self.pos = clamp(c.f - 2, 0, self.frets - 3);
                    self.render();
                    if (self.opts.onPos) self.opts.onPos(self.pos);
                }
                self._drawCursor();
                if (self.opts.onCursor) self.opts.onCursor(c.s, c.f);
            }
        });
    };

    // ────────────────────────────────── knob ──────────────────────────────
    /*
     * A rotary knob that IS a range input underneath: keyboard, screen
     * readers and form semantics come for free; the dial is decoration that
     * also takes a drag - up or right turns it up (or the wheel, or a
     * double-click to reset). Up is the desk convention; right is what a hand
     * on a phone tries first on a small round control, and it used to do
     * nothing at all (report 30.09, 320 px phone). The axis is the one the
     * finger takes past 4 px; the other is ignored for the rest of the drag.
     * Right-to-left pages mirror the dial, so there "left" turns it up.
     */
    var knobSeq = 0;
    function Knob(parent, o) {
        this.o = o;
        var id = 'egk' + (++knobSeq);
        var wrap = document.createElement('div');
        wrap.className = 'eg-knob';
        var dial = document.createElement('div');
        dial.className = 'dial';
        var ptr = document.createElement('div');
        ptr.className = 'ptr';
        dial.appendChild(ptr);
        var input = document.createElement('input');
        input.type = 'range'; input.id = id;
        input.min = o.min === undefined ? 0 : o.min;
        input.max = o.max === undefined ? 1 : o.max;
        input.step = o.step || 0.01;
        input.value = o.value === undefined ? 0.5 : o.value;
        var lab = document.createElement('label');
        lab.setAttribute('for', id);
        lab.textContent = o.label;
        // the name a screen reader says: with its pedal ("Drive - Level"), not one of three "Level"s
        if (o.aria) input.setAttribute('aria-label', o.aria);
        var val = document.createElement('span');
        val.className = 'val';
        dial.appendChild(input);
        wrap.appendChild(dial); wrap.appendChild(lab); wrap.appendChild(val);
        parent.appendChild(wrap);
        this.wrap = wrap; this.dial = dial; this.input = input; this.valEl = val;
        var self = this;
        input.addEventListener('input', function () { self._show(); if (o.onInput) o.onInput(+input.value, self); });
        input.addEventListener('change', function () { if (o.onChange) o.onChange(+input.value, self); });
        var drag = null;
        dial.addEventListener('pointerdown', function (e) {
            e.preventDefault();
            input.focus({ preventScroll: true });
            var rtl = getComputedStyle(dial).direction === 'rtl';
            drag = { x: e.clientX, y: e.clientY, v: +input.value, axis: null, rtl: rtl };
            try { dial.setPointerCapture(e.pointerId); } catch (x) {}
        });
        dial.addEventListener('pointermove', function (e) {
            if (!drag) return;
            var up = drag.y - e.clientY, right = (e.clientX - drag.x) * (drag.rtl ? -1 : 1);
            if (!drag.axis) {
                if (Math.abs(up) < 4 && Math.abs(right) < 4) return;
                drag.axis = Math.abs(right) > Math.abs(up) ? 'x' : 'y';
            }
            var span = (+input.max) - (+input.min);
            var dv = (drag.axis === 'x' ? right : up) / 160 * span * (e.shiftKey ? 0.25 : 1);
            self.set(drag.v + dv, true);
        });
        function end() {
            if (!drag) return;
            if (drag.axis && +input.value !== drag.v) Knob.drags[drag.axis]++;
            drag = null;
            if (o.onChange) o.onChange(+input.value, self);
        }
        dial.addEventListener('pointerup', end);
        dial.addEventListener('pointercancel', end);
        dial.addEventListener('wheel', function (e) {
            if (document.activeElement !== input) return;
            e.preventDefault();
            var span = (+input.max) - (+input.min);
            self.set(+input.value - Math.sign(e.deltaY) * span / 50, true);
        }, { passive: false });
        dial.addEventListener('dblclick', function () {
            if (o.def !== undefined) { self.set(o.def, true); if (o.onChange) o.onChange(+input.value, self); }
        });
        this._show();
    }
    /* drags that turned a knob, by the axis the finger chose (telemetry: does anyone turn sideways?) */
    Knob.drags = { x: 0, y: 0 };
    Knob.prototype.set = function (v, fire) {
        var i = this.input;
        v = clamp(+v, +i.min, +i.max);
        var st = +i.step || 0;
        if (st) v = Math.round(v / st) * st;
        i.value = v;
        this._show();
        if (fire && this.o.onInput) this.o.onInput(+i.value, this);
    };
    Knob.prototype._show = function () {
        var i = this.input, n = ((+i.value) - (+i.min)) / (((+i.max) - (+i.min)) || 1);
        this.dial.style.setProperty('--v', n.toFixed(3));
        var txt = this.o.fmt ? this.o.fmt(+i.value) : String(Math.round(n * 10 * 10) / 10);
        this.valEl.textContent = txt;
        i.setAttribute('aria-valuetext', txt);
    };

    // ───────────────────────────── pedal box ──────────────────────────────
    /*
     * def: { key, name, hue, on, select: {options:[[v,label]], value, onChange},
     *        knobs: [{k, label, value, def, fmt}], onToggle(on), onKnob(k, v) }
     */
    function Pedal(parent, def, T) {
        var box = document.createElement('div');
        box.className = 'eg-pedal' + (def.on ? '' : ' off');
        box.style.setProperty('--p-h', def.hue);
        var top = document.createElement('div');
        top.className = 'eg-pedal-top';
        var led = document.createElement('span'); led.className = 'eg-led';
        var nm = document.createElement('span'); nm.className = 'eg-pedal-name'; nm.textContent = def.name;
        top.appendChild(led); top.appendChild(nm);
        var sel = null;
        if (def.select) {
            sel = document.createElement('select');
            sel.className = 'eg-sel';
            sel.setAttribute('aria-label', def.name);
            def.select.options.forEach(function (o) {
                var op = document.createElement('option'); op.value = o[0]; op.textContent = o[1]; sel.appendChild(op);
            });
            sel.value = def.select.value;
            sel.addEventListener('change', function () { def.select.onChange(sel.value); });
            top.appendChild(sel);
        }
        var sw = document.createElement('button');
        sw.type = 'button'; sw.className = 'eg-sw';
        sw.setAttribute('aria-pressed', def.on ? 'true' : 'false');
        sw.setAttribute('aria-label', def.name);
        sw.textContent = def.on ? T.on : T.off;
        top.appendChild(sw);
        box.appendChild(top);
        var kn = document.createElement('div');
        kn.className = 'eg-knobs';
        box.appendChild(kn);
        parent.appendChild(box);
        var knobs = {};
        (def.knobs || []).forEach(function (k) {
            knobs[k.k] = new Knob(kn, {
                label: k.label, aria: def.name + ' — ' + k.label, value: k.value, def: k.def, min: k.min, max: k.max, step: k.step, fmt: k.fmt,
                onInput: function (v) { def.onKnob(k.k, v); },
                onChange: function (v) { if (def.onKnobDone) def.onKnobDone(k.k, v); }
            });
        });
        var api = {
            box: box, knobs: knobs, sel: sel,
            setOn: function (on) {
                box.classList.toggle('off', !on);
                sw.setAttribute('aria-pressed', on ? 'true' : 'false');
                sw.textContent = on ? T.on : T.off;
            },
            /* a new preset: the same pedal, other settings (nothing is fired) */
            update: function (on, selValue, values) {
                api.setOn(!!on);
                if (sel && selValue) sel.value = selValue;
                for (var k in knobs) if (Object.prototype.hasOwnProperty.call(knobs, k)) {
                    var v = values && values[k];
                    knobs[k].set(v !== undefined && v !== null ? v : knobs[k].o.def);
                }
            }
        };
        sw.addEventListener('click', function () {
            var on = sw.getAttribute('aria-pressed') !== 'true';
            api.setOn(on);
            def.onToggle(on);
        });
        return api;
    }

    /* A little tablature strip for the riff: six lines, fret numbers, a playhead. */
    function Tab(host) { this.host = host; this.svg = null; this.head = null; this.beats = 8; this.x0 = 34; this.px = 44; }
    Tab.prototype.render = function (cells, beats, strings, names) {
        this.beats = beats;
        var host = this.host;
        while (host.firstChild) host.removeChild(host.firstChild);
        var px = this.px, x0 = this.x0, row = 16;
        var W = x0 + beats * px + 12, H = row * (strings + 1);
        var svg = el('svg', { viewBox: '0 0 ' + W + ' ' + H, width: W, height: H, role: 'img' }, host);
        for (var s = 0; s < strings; s++) {
            var y = row * (s + 1);
            el('line', { x1: x0 - 6, y1: y, x2: W - 6, y2: y }, svg);
            var sn = el('text', { x: 6, y: y + 4, class: 'sn' }, svg); sn.textContent = names ? names[s] : '';
        }
        for (var b = 0; b <= beats; b += 4) el('line', { x1: x0 + b * px - 4, y1: row, x2: x0 + b * px - 4, y2: row * strings }, svg);
        this.head = el('rect', { x: x0 - 4, y: row / 2, width: 6, height: row * strings, class: 'ph' }, svg);
        for (var i = 0; i < cells.length; i++) {
            var c = cells[i];
            var cx = x0 + c.beat * px + 2, cy = row * (c.s + 1);
            var label = String(c.fret);
            if (/\bx\b/.test(c.flags)) label = 'x';
            var w = 8 + label.length * 7;
            el('rect', { x: cx - 3, y: cy - 7, width: w, height: 14, rx: 3, class: 'cell' }, svg);
            var t = el('text', { x: cx, y: cy + 4 }, svg);
            var suf = /\bpm\b/.test(c.flags) ? '·' : (/b2/.test(c.flags) ? 'b' : (/\bv\b/.test(c.flags) ? '~' : ''));
            t.textContent = label + suf;
        }
        this.svg = svg;
        this.W = W;
    };
    Tab.prototype.at = function (beat) {
        if (!this.head) return;
        this.head.setAttribute('x', this.x0 + beat * this.px - 4);
        // keep the playhead in view inside the strip (container scroll, never the page)
        var x = this.x0 + beat * this.px, host = this.host;
        if (x < host.scrollLeft + 20 || x > host.scrollLeft + host.clientWidth - 40) host.scrollLeft = Math.max(0, x - 60);
    };

    return { Neck: Neck, Knob: Knob, Pedal: Pedal, Tab: Tab, noteName: noteName, spelledName: spelledName, setSolfege: setSolfege };
}));
